import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useScrollSpy } from '../hooks/useScrollSpy';
import { useUser } from '@clerk/clerk-react';
import { FolderIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ProgramCard from '../components/programs/ProgramCard';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import Input from '../components/ui/Input';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import ToolkitListRow from '../components/hubs/ToolkitListRow';
import PlaceLink from '../components/layout/PlaceLink';
import { scrollToSection } from '../components/layout/OnThisPage';
import ResourceCard from '../components/resources/ResourceCard';
import { readProfileExtras } from '../types/userProfile';
import { hubAPI } from '../services/hubApi';
import { programAPI } from '../services/programApi';
import { fetchCatalogResources } from '../services/resourceCatalogApi';
import { wordpressAPI } from '../services/wordpressApi';
import { Hub } from '../types/hub';
import { Program } from '../types/program';
import { CatalogResource } from '../types/resourceCatalog';
import { WPPost } from '../types/wordpress';
import { ORBCON_REPORT_CATEGORY_ID } from '../content/orbconReport';
import { hubPlaceHref } from '../utils/hubChapter';
import { filterHubs, hubHasDocuments, programMatchesQuery } from '../utils/hubSearch';
import { placePath } from '../utils/placePaths';
import { matchingPlaces } from '../utils/libraryPlaces';
import { placedDocumentIds, rankCatalogResources } from '../utils/searchRank';
import { isUnderRevision } from '../utils/resourceRevision';
import { useCatalogTopicsByWpId } from '../hooks/useCatalogTopicsByWpId';
import {
  effectiveInterestTopics,
  hubResourceWpIds,
  programResourceWpIds,
  topicListsForWpIds,
} from '../utils/placeInterests';

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function Reveal({ show, children }: { show: boolean; children: ReactNode }) {
  const skip = useRef(true);
  const held = useRef(children);
  const [rendered, setRendered] = useState(show);
  const [visible, setVisible] = useState(show);
  if (show) held.current = children;

  useEffect(() => {
    if (skip.current) {
      skip.current = false;
      return;
    }
    if (show) {
      setRendered(true);
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    }
    setVisible(false);
    if (prefersReducedMotion()) {
      setRendered(false);
      return;
    }
    const timer = window.setTimeout(() => setRendered(false), 200);
    return () => window.clearTimeout(timer);
  }, [show]);

  if (!rendered) return null;

  return (
    <div
      className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none ${
        visible ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      }`}
    >
      <div className="min-h-0 overflow-hidden">{show ? children : held.current}</div>
    </div>
  );
}

function catalogAsPost(resource: CatalogResource, wp?: WPPost): WPPost {
  const date = resource.wpCreatedAt || resource.wpModifiedAt || '';
  const underRevision = wp
    ? isUnderRevision(wp.acf?.under_revision)
    : resource.underRevision === true;
  return {
    id: resource.wpId,
    date,
    modified: wp?.modified || resource.wpModifiedAt || date,
    slug: resource.slug,
    parent: wp?.parent,
    link: resource.url,
    title: { rendered: resource.title },
    content: { rendered: '' },
    excerpt: { rendered: '' },
    featured_media: 0,
    categories: wp?.categories ?? [],
    tags: [],
    author: 0,
    acf: underRevision ? { under_revision: true } : undefined,
  };
}

export default function Programs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSignedIn, user } = useUser();
  const query = searchParams.get('q') || '';
  const [programs, setPrograms] = useState<Program[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [documents, setDocuments] = useState<WPPost[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [programData, hubData] = await Promise.all([
          programAPI.fetchPrograms(),
          hubAPI.fetchHubs(),
        ]);
        setPrograms(programData);
        setHubs(hubData);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load resources'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (loading || window.location.hash !== '#toolkits') return;
    document.getElementById('toolkits')?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [loading]);

  const visiblePrograms = useMemo(
    () => (query.trim() ? programs.filter((program) => programMatchesQuery(program, query)) : programs),
    [programs, query]
  );
  const visibleHubs = useMemo(() => {
    const withDocuments = hubs.filter((hub) => hubHasDocuments(hub));
    const matchedProgramIds = new Set(
      query.trim() ? programs.filter((program) => programMatchesQuery(program, query)).map((program) => program.id) : []
    );
    const pool = query.trim()
      ? withDocuments.filter((hub) => !hub.programId || !matchedProgramIds.has(hub.programId))
      : withDocuments.filter((hub) => !hub.program);
    return [...filterHubs(pool, query)].sort((a, b) => a.title.localeCompare(b.title));
  }, [hubs, programs, query]);
  const queryActive = query.trim().length > 0;
  const hubsListedOnPrograms = queryActive && visiblePrograms.length > 0 && visibleHubs.length === 0;
  const startPlace = useMemo(
    () => (query.trim() ? matchingPlaces(query, programs, hubs)[0] ?? null : null),
    [query, programs, hubs]
  );
  const startProgram = startPlace?.kind === 'program' ? programs.find((program) => program.id === startPlace.id) ?? null : null;
  const startHub = startPlace?.kind === 'hub' ? hubs.find((hub) => hub.id === startPlace.id) ?? null : null;
  const subjectCards = visiblePrograms.filter((program) => program.kind !== 'REFERENCE' && program.id !== startProgram?.id);
  const referenceCards = visiblePrograms.filter((program) => program.kind === 'REFERENCE' && program.id !== startProgram?.id);
  const listedHubs = visibleHubs.filter((hub) => hub.id !== startHub?.id);
  const interestTopics = useMemo(
    () => readProfileExtras(user?.unsafeMetadata).topics ?? [],
    [user?.unsafeMetadata]
  );
  const interestSourceWpIds = useMemo(
    () => [
      ...new Set([
        ...programs.flatMap((program) => programResourceWpIds(program)),
        ...hubs.flatMap((hub) => hubResourceWpIds(hub)),
      ]),
    ],
    [programs, hubs]
  );
  const { topicsByWpId } = useCatalogTopicsByWpId(interestSourceWpIds);
  const suggestions = useMemo(() => {
    const subjects = programs.filter((program) => program.kind !== 'REFERENCE');
    if (isSignedIn && interestTopics.length > 0) {
      const matchedPrograms = subjects.filter((program) => {
        const effective = effectiveInterestTopics(
          program.interestTopics,
          topicListsForWpIds(programResourceWpIds(program), topicsByWpId)
        );
        return effective.some((topic) => interestTopics.includes(topic));
      });
      const matchedHubs = hubs.filter((hub) => {
        if (!hubHasDocuments(hub) || hub.program) return false;
        const effective = effectiveInterestTopics(
          hub.interestTopics,
          topicListsForWpIds(hubResourceWpIds(hub), topicsByWpId)
        );
        return effective.some((topic) => interestTopics.includes(topic));
      });
      const matched = [
        ...matchedPrograms.map((program) => ({ kind: 'program' as const, program })),
        ...matchedHubs.map((hub) => ({ kind: 'hub' as const, hub })),
      ];
      if (matched.length > 0) return matched.slice(0, 3);
    }
    return [...subjects]
      .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title))
      .slice(0, 3)
      .map((program) => ({ kind: 'program' as const, program }));
  }, [programs, hubs, isSignedIn, interestTopics, topicsByWpId]);
  const programCount = subjectCards.length + (startProgram && startProgram.kind !== 'REFERENCE' ? 1 : 0);
  const toolkitCount = listedHubs.length + (startHub ? 1 : 0);
  const referenceCount = referenceCards.length + (startProgram?.kind === 'REFERENCE' ? 1 : 0);
  const groups = [
    programCount > 0 ? { id: 'programs', label: 'Programs', count: programCount } : null,
    toolkitCount > 0 ? { id: 'toolkits', label: 'Toolkits', count: toolkitCount } : null,
    referenceCount > 0 ? { id: 'references', label: 'References', count: referenceCount } : null,
    documents.length > 0 ? { id: 'documents', label: 'Resources', count: documents.length } : null,
  ].filter((group): group is { id: string; label: string; count: number } => Boolean(group));
  const currentGroup = useScrollSpy(
    groups.map((group) => group.id),
    176
  );
  const nothingMatched =
    queryActive &&
    visiblePrograms.length === 0 &&
    visibleHubs.length === 0 &&
    documents.length === 0 &&
    !documentsLoading;

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setDocuments([]);
      setDocumentsLoading(false);
      return;
    }
    let cancelled = false;
    setDocumentsLoading(true);
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({ q, perPage: '10', sort: 'relevance' });
      fetchCatalogResources(params)
        .then(async (catalog) => {
          const wpPosts = catalog.data.length
            ? await wordpressAPI
                .fetchPosts({
                  include: catalog.data.map((resource) => resource.wpId).join(','),
                  per_page: catalog.data.length,
                })
                .catch(() => [] as WPPost[])
            : [];
          if (cancelled) return;
          const wpById = new Map(wpPosts.map((post) => [post.id, post]));
          const ranked = rankCatalogResources(
            catalog.data,
            wpById,
            placedDocumentIds(hubs, programs),
            ORBCON_REPORT_CATEGORY_ID
          );
          setDocuments(
            [...ranked.documents, ...ranked.news].map((resource) => catalogAsPost(resource, wpById.get(resource.wpId)))
          );
        })
        .catch(() => {
          if (!cancelled) setDocuments([]);
        })
        .finally(() => {
          if (!cancelled) setDocumentsLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, hubs, programs]);

  const updateParams = (nextQuery: string) => {
    const next = new URLSearchParams();
    if (nextQuery) next.set('q', nextQuery);
    setSearchParams(next, { replace: true });
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={2} className="mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <LoadingSkeleton key={i} lines={5} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState error={error} title="Failed to load resources" onRetry={() => window.location.reload()} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white">
      <h1 className="text-3xl font-bold text-gray-900">Resources</h1>
      {groups.length > 0 && (
        <div className="sticky top-24 z-30 -mx-4 mt-2 mb-6 border-b border-gray-200 bg-white px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <nav aria-label="Resource groups" className="flex gap-2">
            {groups.map((group) => (
              <a
                key={group.id}
                href={`#${group.id}`}
                onClick={(event) => scrollToSection(event, group.id)}
                className={`inline-flex items-center min-h-[44px] px-2 text-sm font-medium border-b-2 focus-ring ${
                  currentGroup === group.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-700 hover:text-primary'
                }`}
                aria-current={currentGroup === group.id ? 'true' : undefined}
              >
                {group.label}
              </a>
            ))}
          </nav>
        </div>
      )}

      {(programs.length > 0 || hubs.length > 0) && (
        <div className="relative mb-6">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <Input
            type="text"
            placeholder="Search resources, programs, and toolkits"
            value={query}
            onChange={(e) => updateParams(e.target.value)}
            className="pl-10 pr-10"
            aria-label="Search resources, programs, and toolkits"
          />
          {query && (
            <button
              type="button"
              onClick={() => updateParams('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      )}

      {!queryActive && suggestions.length > 0 && (
        <nav aria-label="Suggested" className="flex flex-wrap items-center">
          <span className="inline-flex items-center min-h-[44px] px-2 text-sm font-medium text-gray-900">
            Suggested
          </span>
          {suggestions.map((item) => {
            const program = item.kind === 'program';
            const title = program ? item.program.title : item.hub.title;
            const slug = program ? item.program.slug : item.hub.slug;
            return (
              <PlaceLink
                key={program ? item.program.id : item.hub.id}
                to={program ? placePath(item.program) : hubPlaceHref(item.hub)}
                surface={{ kind: program ? 'program' : 'hub', slug, via: 'link' }}
                className="inline-flex items-center min-h-[44px] px-2 text-sm text-gray-600 hover:text-primary focus-ring rounded-sm"
              >
                {title}
              </PlaceLink>
            );
          })}
        </nav>
      )}

      <Reveal show={Boolean(startProgram || startHub)}>
        <section className="mt-16" aria-labelledby="start-here-heading">
          <h2 id="start-here-heading" className="text-2xl font-semibold text-gray-900 mb-5">
            Start here
          </h2>
          {startProgram && startProgram.kind !== 'REFERENCE' && subjectCards.length === 0 && (
            <div id="programs" className="scroll-mt-36" />
          )}
          {startHub && listedHubs.length === 0 && <div id="toolkits" className="scroll-mt-36" />}
          {startProgram?.kind === 'REFERENCE' && referenceCards.length === 0 && (
            <div id="references" className="scroll-mt-36" />
          )}
          {startProgram && (
            <ProgramCard
              program={startProgram}
              query={query}
              anchorId={`place-${startProgram.slug}`}
              highlighted
            />
          )}
          {startHub && (
            <ToolkitListRow hub={startHub} query={query} anchorId={`place-${startHub.slug}`} highlighted />
          )}
        </section>
      </Reveal>

      <Reveal show={subjectCards.length > 0}>
        <section id="programs" className="mt-16 scroll-mt-36" aria-labelledby="programs-heading">
          <h2 id="programs-heading" className="text-2xl font-semibold text-gray-900 mb-5">
            Programs
            <span className="ml-3 text-lg font-normal text-gray-500">{subjectCards.length}</span>
          </h2>
          <div className="space-y-4">
            {subjectCards.map((program) => (
              <ProgramCard
                key={program.id}
                program={program}
                query={query}
                anchorId={`place-${program.slug}`}
              />
            ))}
          </div>
        </section>
      </Reveal>

      <Reveal show={(!queryActive || listedHubs.length > 0) && !hubsListedOnPrograms && listedHubs.length > 0}>
      <section id="toolkits" aria-labelledby="hubs-heading" className="mt-16 scroll-mt-36">
        <h2 id="hubs-heading" className="text-2xl font-semibold text-gray-900 mb-5">
          Toolkits
          <span className="ml-3 text-lg font-normal text-gray-500">{listedHubs.length}</span>
        </h2>
        <div className="space-y-4">
          {listedHubs.map((hub) => (
            <ToolkitListRow key={hub.id} hub={hub} query={query} anchorId={`place-${hub.slug}`} />
          ))}
        </div>
      </section>
      </Reveal>

      <Reveal show={referenceCards.length > 0}>
        <section id="references" className="mt-16 scroll-mt-36" aria-labelledby="references-heading">
          <h2 id="references-heading" className="text-2xl font-semibold text-gray-900 mb-5">
            References
            <span className="ml-3 text-lg font-normal text-gray-500">{referenceCards.length}</span>
          </h2>
          <div className="space-y-4">
            {referenceCards.map((program) => (
              <ProgramCard
                key={program.id}
                program={program}
                query={query}
                anchorId={`place-${program.slug}`}
              />
            ))}
          </div>
        </section>
      </Reveal>

      {documentsLoading && queryActive && documents.length === 0 && (
        <LoadingSkeleton lines={4} className="mt-16" />
      )}

      <Reveal show={documents.length > 0}>
        <section id="documents" className="mt-16 scroll-mt-36" aria-labelledby="documents-heading">
          <h2 id="documents-heading" className="text-2xl font-semibold text-gray-900 mb-5">
            Resources
            <span className="ml-3 text-lg font-normal text-gray-500">{documents.length}</span>
          </h2>
          <div className="space-y-4">
            {documents.map((post) => (
              <ResourceCard key={post.id} post={post} view="list" />
            ))}
          </div>
        </section>
      </Reveal>

      {nothingMatched && (
        <EmptyState
          icon={<FolderIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />}
          title="No resources found"
          description={`Nothing matches “${query}”. Try a different search.`}
        />
      )}
    </div>
  );
}
